import React, { useState, useEffect, useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import SearchPage from "./pages/SearchPage";
import TallerDetailPage from "./pages/TallerDetailPage";
import ClientDashboard from "./pages/ClientDashboard";
import TallerDashboard from "./pages/TallerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ServiceRequestForm from "./pages/ServiceRequestForm";
import VehicleManagement from "./pages/VehicleManagement";
import CarPassportPage from "./pages/CarPassportPage";
import PublicPassportPage from "./pages/PublicPassportPage";
import DemoTourPage from "./pages/DemoTourPage";
import ServiceRequestDetail from "./pages/ServiceRequestDetail";
import BidsView from "./pages/BidsView";
import CompareOffersPage from "./pages/CompareOffersPage";
import ChatPage from "./pages/ChatPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    // TODO: [SECURITY] Migrate from localStorage to httpOnly cookies for token storage
    // localStorage is vulnerable to XSS attacks. See: https://owasp.org/www-community/attacks/xss/
    const token = localStorage.getItem("session_token");
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        setSessionToken(token);
      } catch (error) {
        // Auth check failed - clear invalid token
        localStorage.removeItem("session_token");
      }
    }
    setLoading(false);
  };

  const getDashboardRoute = (userType) => {
    if (userType === "client") return "/client/dashboard";
    if (userType === "taller") return "/taller/dashboard";
    if (userType === "admin") return "/admin/dashboard";
    return "/";
  };

  const handleLogin = (userData, token) => {
    setUser(userData);
    setSessionToken(token);
    // TODO: [SECURITY] Migrate to httpOnly cookie set by backend
    localStorage.setItem("session_token", token);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      localStorage.removeItem("session_token");
      setUser(null);
      setSessionToken(null);
      window.location.href = "/";
    } catch (error) {
      // Logout failed - still clear local state
      localStorage.removeItem("session_token");
      setUser(null);
      setSessionToken(null);
      window.location.href = "/";
    }
  };

  // Memoize context value to prevent unnecessary re-renders
  // IMPORTANT: Must be before any conditional returns to maintain hook order
  const authContextValue = useMemo(() => ({
    user, sessionToken, handleLogin, handleLogout, API
  }), [user, sessionToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-2xl font-semibold text-indigo-600">Loading Flix...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/taller/:tallerId" element={<TallerDetailPage />} />
            <Route path="/car-passport/public/:vehicleId" element={<PublicPassportPage />} />
            <Route path="/demo" element={<DemoTourPage />} />
            
            {/* Client Routes */}
            <Route
              path="/client/dashboard"
              element={user && user.user_type === "client" ? <ClientDashboard /> : <Navigate to="/auth" />}
            />
            <Route
              path="/client/vehicles"
              element={user && user.user_type === "client" ? <VehicleManagement /> : <Navigate to="/auth" />}
            />
            <Route
              path="/client/car-passport/:vehicleId"
              element={user && user.user_type === "client" ? <CarPassportPage /> : <Navigate to="/auth" />}
            />
            <Route
              path="/client/request-service"
              element={user && user.user_type === "client" ? <ServiceRequestForm /> : <Navigate to="/auth" />}
            />
            <Route
              path="/client/bids/:requestId"
              element={user && user.user_type === "client" ? <BidsView /> : <Navigate to="/auth" />}
            />
            <Route
              path="/client/compare"
              element={user && user.user_type === "client" ? <CompareOffersPage /> : <Navigate to="/auth" />}
            />
            <Route
              path="/client/request/:requestId"
              element={user && user.user_type === "client" ? <ServiceRequestDetail /> : <Navigate to="/auth" />}
            />
            
            {/* Taller Routes */}
            <Route
              path="/taller/dashboard"
              element={user && user.user_type === "taller" ? <TallerDashboard /> : <Navigate to="/auth" />}
            />
            
            {/* Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={user && user.user_type === "admin" ? <AdminDashboard /> : <Navigate to="/auth" />}
            />
            
            {/* Common Routes */}
            <Route
              path="/chat/:serviceRequestId"
              element={user ? <ChatPage /> : <Navigate to="/auth" />}
            />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
